// WPCRegistration.jsx
import React from "react";

export default function WPCRegistration() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            WPC Certificate
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            WPC ETA Certificate for Wireless Devices
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks assists businesses in obtaining WPC Certificates
            (Equipment Type Approval - ETA) for wireless and radio equipment compliance
            through the Saral Sanchar Portal, mandatory for Wi-Fi, Bluetooth, and RFID devices.
          </p>
          <p className="text-gray-700">
            From RF testing coordination through NABL/ILAC accredited labs to WPC
            application submission and Authorized Indian Representative (AIR) appointment,
            the process ensures legal import, manufacturing, and sale of wireless products in India.
          </p>
        </div>

        {/* Right: Wireless technology / antenna testing image */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/356056/pexels-photo-356056.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Wireless technology RF testing for WPC ETA compliance"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks provides end-to-end WPC ETA certification support
              through the Wireless Planning & Coordination Wing (DoT) for all de-licensed
              frequency wireless devices.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  RF testing coordination with NABL/ILAC accredited labs for unlicensed
                  frequency bands (2.4GHz Wi-Fi, Bluetooth, Zigbee, RFID compliance).
                </li>
                <li>
                  Saral Sanchar Portal registration, ETA Form-1 application preparation,
                  and government fee payment (₹10,000) management.
                </li>
                <li>
                  Authorized Indian Representative (AIR) appointment for foreign
                  manufacturers and document submission to WPC regional offices.
                </li>
                <li>
                  ETA certificate issuance (valid 3-5 years) and label compliance guidance
                  for market-ready wireless products.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for WPC Certificate
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks assists in compiling technical documentation
              required for WPC ETA approval of wireless equipment.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Product technical specifications including frequency range, modulation type, power output, and antenna details.</li>
                <li>
                  Test reports from NABL/ILAC accredited labs confirming compliance
                  with de-licensed frequency emission limits and human safety standards.
                </li>
                <li>
                  Importer/manufacturer details with IEC, GST, business registration,
                  and Authorized Indian Representative (AIR) appointment letter.
                </li>
                <li>
                  User manual, block diagram, circuit schematics, and compliance
                  declaration for WPC technical parameter verification.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm specializes in wireless regulatory compliance with proven
              success securing WPC ETA approvals for Wi-Fi, Bluetooth, and IoT devices.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Wireless Compliance Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Complete mastery of WPC de-licensed frequency regulations, RF
                  testing requirements, and Saral Sanchar Portal procedures.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Technical Documentation
                </h3>
                <p className="text-gray-700 text-sm">
                  Professional test report structuring, ETA Form-1 preparation,
                  and WPC regional office coordination ensuring first-time approvals.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Professional Regulatory Support
                </h3>
                <p className="text-gray-700 text-sm">
                  End-to-end guidance from lab testing through ETA issuance, AIR
                  appointment, and ongoing compliance for wireless product lifecycle.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Launch Wireless Products Legally
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to secure your WPC ETA Certificate
                and legally import, manufacture, and sell Wi-Fi, Bluetooth, and wireless
                devices across India's consumer electronics and IoT markets.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
